import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { YachtItem, ReservationRequest, YachtAvailability } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  CalendarDays,
  Anchor,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  Users,
  AlertCircle,
  Wrench,
} from 'lucide-react';

export const ReservationCalendar: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite } = useSite();
  const [yachts, setYachts] = useState<YachtItem[]>([]);
  const [reservations, setReservations] = useState<ReservationRequest[]>([]);
  const [availabilities, setAvailabilities] = useState<YachtAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!activeSite) return;
    setLoading(true);
    Promise.all([
      dbService.getYachts(activeSite.id),
      dbService.getReservations(activeSite.id),
      dbService.getYachtAvailability(activeSite.id),
    ])
      .then(([yachtData, resData, availData]) => {
        setYachts(yachtData || []);
        setReservations(resData || []);
        setAvailabilities(availData || []);
      })
      .finally(() => setLoading(false));
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Doluluk & Rezervasyon Takvimi İçin Aktif Web Sitesi Gereklidir"
        description="Filo müsaitlik durumunu, opsiyonlu günleri ve onaylanan kiralama takvimini takip etmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-600">
            <CalendarDays className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Yat Kiralama Portalı</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Yat Müsaitlik & Doluluk Takvimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Filonuzdaki teknelerin kiralama dönemlerini ve müsaitlik durumunu görsel takvim üzerinden takip edin.
          </p>
        </div>

        {/* Month Selector */}
        <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
          <button
            onClick={handlePrevMonth}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-bold text-xs text-slate-900 min-w-[120px] text-center">
            {monthNames[currentMonth]} {currentYear}
          </span>
          <button
            onClick={handleNextMonth}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Calendar Grid Matrix */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex flex-wrap items-center gap-6 text-xs font-semibold text-slate-600 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-emerald-500" />
            <span>Müsait</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-cyan-600" />
            <span>Onaylı Rezervasyon (Dolu)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-500" />
            <span>Bekleyen Talep (Opsiyon)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-purple-600" />
            <span>Bakım / Manuel Blok</span>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-xs text-slate-400">Takvim yükleniyor...</div>
        ) : yachts.length === 0 ? (
          <div className="py-12 text-center space-y-2">
            <Anchor className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="font-bold text-sm text-slate-700">Filonuzda Henüz Tekne Bulunmuyor</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Takvimde teknelerin doluluk durumunu takip edebilmek için önce Yat Yönetimi menüsünden tekne ekleyin.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[850px] space-y-4">
              {yachts.map((yacht) => {
                const yachtBookings = reservations.filter(
                  (r) => !r.isDeleted && r.yachtId === yacht.id && r.status !== 'CANCELLED'
                );

                return (
                  <div key={yacht.id} className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg overflow-hidden border border-slate-300 shrink-0 bg-slate-200">
                          {yacht.coverImage ? (
                            <img src={yacht.coverImage} alt={yacht.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <Anchor className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-xs text-slate-900">{yacht.name}</div>
                          <div className="text-[10px] text-slate-500">
                            {yacht.type} • {yacht.portLocation || yacht.basePort || 'Liman belirtilmemiş'} • {yacht.pricePerDay} {yacht.currency || 'EUR'}/gün
                          </div>
                        </div>
                      </div>

                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                        {yachtBookings.length} Aktif Kayıt
                      </span>
                    </div>

                    {/* Day Blocks in the Month */}
                    <div className="grid grid-cols-31 gap-1">
                      {daysArray.map((day) => {
                        const targetDateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

                        // Check confirmed reservations
                        const confirmedRes = yachtBookings.find(
                          (b) =>
                            (b.status === 'CONFIRMED' || b.status === 'COMPLETED') &&
                            b.startDate <= targetDateStr &&
                            b.endDate >= targetDateStr
                        );

                        // Check pending requests
                        const pendingRes = !confirmedRes && yachtBookings.find(
                          (b) =>
                            (b.status === 'PENDING' || b.status === 'CONTACTED') &&
                            b.startDate <= targetDateStr &&
                            b.endDate >= targetDateStr
                        );

                        // Check manual blocks
                        const manualBlock = !confirmedRes && !pendingRes && availabilities.find(
                          (a) =>
                            !a.isDeleted &&
                            a.yachtId === yacht.id &&
                            a.status !== 'AVAILABLE' &&
                            a.startDate <= targetDateStr &&
                            a.endDate >= targetDateStr
                        );

                        let statusClass = 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100';
                        let titleText = `${day} ${monthNames[currentMonth]}: Müsait`;

                        if (confirmedRes) {
                          statusClass = 'bg-cyan-600 text-white shadow-sm';
                          titleText = `${day} ${monthNames[currentMonth]}: Onaylı Rezervasyon (${confirmedRes.customerName})`;
                        } else if (pendingRes) {
                          statusClass = 'bg-amber-500 text-white shadow-sm';
                          titleText = `${day} ${monthNames[currentMonth]}: Bekleyen Talep (${pendingRes.customerName})`;
                        } else if (manualBlock) {
                          statusClass = 'bg-purple-600 text-white shadow-sm';
                          titleText = `${day} ${monthNames[currentMonth]}: ${manualBlock.notes || 'Bakım / Blok'}`;
                        }

                        return (
                          <div
                            key={day}
                            className={`h-7 rounded flex flex-col items-center justify-center text-[10px] font-bold transition cursor-default ${statusClass}`}
                            title={titleText}
                          >
                            {day}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
